//src/pages/Stocks/EtfPage.jsx
//src/pages/Stocks/CryptoPage.jsx
import React, { useEffect, useState } from "react";
import { Tabs } from "antd";
import CandleChart from "../../components/CandleChart";
import SEO from "../../components/SEO";
import StockDisclaimer from "../../components/StockDisclaimer";
import "../../styles/ticker.css";
import Ticker from "../../components/Ticker";

import { safeFetch } from "../../components/safeFetch"; // <

import TableHeader from "../../components/table/TableHeader";
import TableBody from "../../components/table/TableBody";
import { useOutletContext } from "react-router-dom";
import { getPort } from "../../utils/getPort";

const { TabPane } = Tabs;

export default function TadawulPage() {
  const [rows, setRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [market, setMarket] = useState("amex"); // NEW: market selector
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [timeframe, setTimeframe] = useState("1d");
  const [viewMode, setViewMode] = useState("table");
  const [ohlcv, setOhlcv] = useState([]);

  const [weeklyRows, setWeeklyRows] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [hour3Rows, setHour3Rows] = useState([]);
  const [hour2Rows, setHour2Rows] = useState([]);
  const [hourlyRows, setHourlyRows] = useState([]);

  const [fastEma, setFastEma] = useState(8);
  const [slowEma, setSlowEma] = useState(16);
  const [slowerEma, setSlowerEma] = useState(24);

  const [useAdaptiveEma, setUseAdaptiveEma] = useState(true);

  const [upperBand, setUpperBand] = useState(1.05);
  const [lowerBand, setLowerBand] = useState(0.953);

  const [sortColumn, setSortColumn] = useState(null);

  const [hoveredColumn, setHoveredColumn] = useState(null);

  const [emaMode, setEmaMode] = useState("trend"); // default

  const [tickerSpeed, setTickerSpeed] = useState(800);

  const { daysBack, volatility, setVolatility, zeroOCR, setZeroOCR } =
    useOutletContext();

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(
      () => {
        setRefreshKey((k) => k + 1);
      },
      5 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  // Load symbols for selected market
  // Load symbols for selected market
  useEffect(() => {
    async function loadSymbols() {
      const data = await safeFetch(`${getPort(market)}/api/symbols/${market}`);
      setRows(data || []);

      
    }
    loadSymbols();
  }, [market]);

  // Load market tables (CSV endpoints)

  useEffect(() => {
    safeFetch(`${getPort(market)}/api/market_table/${market}/1week`).then(
      (data) => setWeeklyRows(data || [])
    );
  }, [refreshKey, market]);

  useEffect(() => {
    safeFetch(`${getPort(market)}/api/market_table/${market}/1d`).then((data) =>
      setDailyRows(data || [])
    );
  }, [refreshKey, market]);

  useEffect(() => {
    safeFetch(`${getPort(market)}/api/market_table/${market}/3h`).then((data) =>
      setHour3Rows(data || [])
    );
  }, [refreshKey, market]);

  useEffect(() => {
    safeFetch(`${getPort(market)}/api/market_table/${market}/2h`).then((data) =>
      setHour2Rows(data || [])
    );
  }, [refreshKey, market]);

  useEffect(() => {
    safeFetch(`${getPort(market)}/api/market_table/${market}/1h`).then((data) =>
      setHourlyRows(data || [])
    );
  }, [refreshKey, market]);

  // Load OHLCV candles (using datetime)
  useEffect(() => {
    if (!selectedSymbol) {
      setOhlcv([]);
      return;
    }

    const url = `${getPort(market)}/api/ohlcv/${market}/${selectedSymbol}/${timeframe}`;

    safeFetch(url).then((data) => setOhlcv(Array.isArray(data) ? data : []));
  }, [selectedSymbol, timeframe, market, refreshKey]);

  useEffect(() => {
    if (!ohlcv || ohlcv.length === 0) return;

    const closes = ohlcv.map((c) => c.close);
    const lastOcr1 = ohlcv[ohlcv.length - 1]?.ocr1 ?? 50;

    async function fetchAdaptiveEma() {
      try {
        const response = await fetch(
          `${getPort(market)}/api/indicators/adaptive-ema`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              closes,
              ocr1: lastOcr1,
              timeframe,
              mode: emaMode, // ← ADD THIS
            }),
          }
        );

        const data = await response.json();

        // 🔥 Convert backend EMA → chart format
        const fast = data.fast_ema.map((v, i) => ({
          time: ohlcv[i].datetime,
          value: v,
        }));

        const slow = data.slow_ema.map((v, i) => ({
          time: ohlcv[i].datetime,
          value: v,
        }));

        const slower = data.slower_ema.map((v, i) => ({
          time: ohlcv[i].datetime,
          value: v,
        }));

        setFastEma(fast);
        setSlowEma(slow);
        setSlowerEma(slower);
      } catch (err) {
        console.error("Adaptive EMA error:", err);
      }
    }

    fetchAdaptiveEma();
  }, [ohlcv, timeframe, emaMode]);

  useEffect(() => {
    setSelectedSymbol(null);
  }, [market]);

  const columnToTimeframe = {
    // ✅ % columns (IMPORTANT)
    3: "1d",
    14: "3h",
    25: "2h",
    36: "1h",
    47: "1week",

    // OCR columns (already working)
    4: "1d", // Daily
    15: "3h", // 3 Hours
    26: "2h", // 2 Hours
    37: "1h", // 1 Hour
    48: "1week", // 30 Minutes
  };

  useEffect(() => {
    if (sortColumn !== null && columnToTimeframe[sortColumn]) {
      setTimeframe(columnToTimeframe[sortColumn]);
    }
  }, [sortColumn]);

  const getPercent = (row) => {
    const prev = row?.closes?.[daysBack];
    const current = row?.close1;

    return Number.isFinite(prev) && Number.isFinite(current) && prev !== 0
      ? ((current - prev) / prev) * 100
      : null;
  };

  // Merge all timeframes into one table row
  const merged = dailyRows.map((d) => {
    const h3 = hour3Rows.find((x) => x.symbol === d.symbol) || {};
    const h2 = hour2Rows.find((x) => x.symbol === d.symbol) || {};
    const h = hourlyRows.find((x) => x.symbol === d.symbol) || {};
    const w1 = weeklyRows.find((x) => x.symbol === d.symbol) || {};

    const prev = d.closes?.[daysBack];

    const daily_percent = getPercent(d);

    const hour3_percent = getPercent(h3);
    const hour2_percent = getPercent(h2);
    const hourly_percent = getPercent(h);
    const weekly_percent = getPercent(w1);

    return {
      symbol: d.symbol,
      name: d.name,

      daily_close: d.close1,
      daily_prev_close: d.close2, // 🔥 yesterday’s close

      //daily_ocr: d.ocr_rank,
      //daily_ocr_values: d.ocr_values,

      // NEW
      daily_ocr_position: d.ocr_position,
      daily_position_values: d.position_values,

      daily_percent,
      daily_volatility: d.volatility, // 🔥 ADD THIS

      hour3_close: h3.close1,

      //hour3_ocr: h3.ocr_rank,
      //hour3_ocr_values: h3.ocr_values,

      // NEW
      hour3_ocr_position: h3.ocr_position,
      hour3_position_values: h3.position_values,

      hour3_percent,
      hour3_volatility: h3.volatility, // 🔥 ADD THIS

      //hour2_close: h2.close1,
      //hour2_ocr: h2.ocr_rank,

      hour2_ocr_values: h2.ocr_values,

      // NEW
      hour2_ocr_position: h2.ocr_position,
      hour2_position_values: h2.position_values,

      hour2_percent,
      hour2_volatility: h2.volatility, // 🔥 ADD THIS

      hour2_close: h3.close1,

      //hourly_ocr: h.ocr_rank,
      //hourly_ocr_values: h.ocr_values,

      // NEW
      hourly_ocr_position: h.ocr_position,
      hourly_position_values: h.position_values,

      hourly_percent,
      hourly_volatility: h.volatility, // 🔥 ADD THIS

      hourly_close: h.close1,

      //weekly_ocr: w1.ocr_rank,
      //weekly_ocr_values: w1.ocr_values,

      // NEW
      weekly_ocr_position: w1.ocr_position,
      weekly_position_values: w1.position_values,

      weekly_percent,
      weekly_volatility: w1.volatility, // 🔥 ADD THIS

      // WEEKLY  🔥 NEW
      weekly_close: w1.close1,
    };
  });

  const th = {
    padding: "8px",
    border: "1px solid #cce4ff",
    fontWeight: "normal",
    textAlign: "center",
    whiteSpace: "nowrap",
    color: "#444",
  };

  const td = {
    padding: "8px",
    border: "1px solid #cce4ff",
    whiteSpace: "nowrap",
    textAlign: "center",
  };

  const rowHighlight = { backgroundColor: "#e6f2ff" };
  const divider = { borderLeft: "3px solid #66a3ff" };

  function getSortedRows() {
    if (sortColumn === null) return merged;

    return [...merged].sort((a, b) => {
      const aVal = getValueForColumn(a, sortColumn);
      const bVal = getValueForColumn(b, sortColumn);
      return aVal - bVal;
    });
  }

  // 2. Build ticker items (same logic for all markets)
  const tickerItems = merged.map((r) => {
    const price =
      r.min5_close ?? r.min15_close ?? r.hourly_close ?? r.daily_close ?? 0;

    const prev = r.daily_prev_close ?? r.daily_close ?? 0;

    const diff = price - prev;
    const pct = prev ? (diff / prev) * 100 : 0;

    return {
      symbol: r.symbol,
      price,
      prev,
      diff,
      pct,
    };
  });

  useEffect(() => {
    const content = document.querySelector(".ticker-content");
    if (!content) return;

    const width = content.scrollWidth; // total pixel width of ticker
    const speed = width / 20; // 20px per second (adjust as you like)

    setTickerSpeed(speed);
  }, [tickerItems]);

  function getValueForColumn(row, colIndex) {
    // ✅ PERCENT SORTING (NEW)
    if (colIndex === 3) return row.daily_percent ?? Infinity;
    if (colIndex === 14) return row.hour3_percent ?? Infinity;
    if (colIndex === 25) return row.hour2_percent ?? Infinity;
    if (colIndex === 36) return row.hourly_percent ?? Infinity;
    if (colIndex === 47) return row.weekly_percent ?? Infinity;

    // DAILY block: columns 3–22
    if (colIndex >= 4 && colIndex <= 13) {
      const rankPos = colIndex - 4; // 0..19
      return row.daily_ocr_position?.[rankPos] ?? Infinity;
    }

    // 3H block: columns 24–43
    if (colIndex >= 15 && colIndex <= 24) {
      const rankPos = colIndex - 15;
      return row.hour3_ocr_position?.[rankPos] ?? Infinity;
    }

    // 2H block: columns 45–64
    if (colIndex >= 26 && colIndex <= 35) {
      const rankPos = colIndex - 26;
      return row.hour2_ocr_position?.[rankPos] ?? Infinity;
    }

    // 1H block: columns 66–85
    if (colIndex >= 37 && colIndex <= 46) {
      const rankPos = colIndex - 37;
      return row.hourly_ocr_position?.[rankPos] ?? Infinity;
    }

    // 30m block: columns 87–106
    if (colIndex >= 48 && colIndex <= 53) {
      const rankPos = colIndex - 48;
      return row.weekly_ocr_position?.[rankPos] ?? Infinity;
    }

    return Infinity;
  }

  function refreshChart() {
    // Simply trigger a re-render by updating state
    setFastEma(fastEma);
    setSlowEma(slowEma);
  }

  const purpleColumns = [4, 15, 26, 37, 48];

  const getCellStyle = (colIndex) => {
    if (purpleColumns.includes(colIndex)) {
      return { backgroundColor: "#e6ccff" }; // light purple
    }
    return {};
  };

  return (
    <>
      <SEO
        title="Live ETF Candle Chart with OCR Indicator — SaudiDune"
        description="Live VOO, SPY and other ETF candle charts with adaptive EMA and OCR mean-reversion indicator."
      />
      <StockDisclaimer />
      <div className="top-sticky">
        <div className="ticker-wrapper">
          <Ticker items={tickerItems} />
        </div>
      </div>

      <Tabs activeKey={viewMode} onChange={(key) => setViewMode(key)}>
        <TabPane tab="Market Table" key="table">
          {/* Backend offline fallback */}
          {rows.length === 0 && (
            <div style={{ color: "#888", padding: "10px", fontSize: "16px" }}>
              Waiting for backend connection…
            </div>
          )}

          {/* Only show the table if rows exist */}
          {rows.length > 0 && (
            <div
              style={{
                marginTop: "45px",
                height: "calc(100vh - 200px)", // space under ticker + controls
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid #cce4ff",
                }}
              >
                <TableHeader
                  th={th}
                  divider={divider}
                  hoveredColumn={hoveredColumn}
                  setSortColumn={setSortColumn}
                />

                <TableBody
                  rows={getSortedRows()}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                  setSelectedSymbol={setSelectedSymbol}
                  setVolatility={setVolatility} // 🔥 ADD THIS
                  timeframe={timeframe} // 🔥 ADD THIS
                  setZeroOCR={setZeroOCR} // 🔥 ADD THIS
                  hoveredColumn={hoveredColumn}
                  setHoveredColumn={setHoveredColumn}
                  td={td}
                  divider={divider}
                  getCellStyle={getCellStyle}
                />
              </table>
            </div>
          )}
        </TabPane>

        <TabPane tab="Candles Chart" key="chart">
          {!selectedSymbol ? (
            <div
              style={{
                margin: "60px auto",
                maxWidth: "600px",
                padding: "40px 30px",
                textAlign: "center",
                background: "#fff8e1",
                border: "2px solid #ffb300",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                color: "#5d4037",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📊</div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#e65100",
                  marginBottom: "10px",
                }}
              >
                No Security Selected
              </div>
              <p style={{ fontSize: "15px", lineHeight: "1.6", margin: 0 }}>
                Please go to the <strong>Market Table</strong> tab and click on
                any security to view its candle chart.
              </p>
              <button
                onClick={() => setViewMode("table")}
                style={{
                  marginTop: "20px",
                  padding: "10px 24px",
                  background: "#ffb300",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Go to Market Table
              </button>
            </div>
          ) : (
            <>
              <div className="chart-scroll-container">
                <CandleChart
                  key={market + selectedSymbol + timeframe}
                  data={ohlcv}
                  fastEma={fastEma}
                  slowEma={slowEma}
                  slowerEma={slowerEma}
                  timeframe={timeframe}
                  upperBand={upperBand}
                  lowerBand={lowerBand}
                  emaMode={emaMode}
                  market={market}
                />
              </div>

              <div
                className="chart-controls-bar"
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "1px",
                  marginBottom: "-50px",
                  alignItems: "center",
                  gap: "40px",
                  padding: "10px 0",
                  height: "auto",
                }}
              >
                <button
                  onClick={() => setRefreshKey((k) => k + 1)}
                  style={{
                    padding: "0 14px",
                    height: "32px",
                    lineHeight: "32px",
                    background: "#1677ff",
                    color: "white",
                    border: "1px solid #1677ff",
                    borderRadius: "4px",
                    cursor: "pointer",
                    outline: "none",
                    transition: "transform 0.15s ease, background 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(1px)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  Refresh
                </button>

                {/* EMA Mode */}
                <label>
                  Mean‑Reversion Mode:
                  <input
                    type="checkbox"
                    checked={emaMode === "mean"}
                    onChange={(e) =>
                      setEmaMode(e.target.checked ? "mean" : "trend")
                    }
                  />
                </label>

                {/* Upper Band */}
                <label style={{ fontWeight: "bold" }}>
                  Upper Band:
                  <input
                    type="number"
                    step="0.01"
                    min="0.8"
                    max="1.1"
                    value={upperBand}
                    onChange={(e) => setUpperBand(parseFloat(e.target.value))}
                    style={{ width: "70px", marginLeft: "6px" }}
                  />
                </label>

                {/* Lower Band */}
                <label style={{ fontWeight: "bold" }}>
                  Lower Band:
                  <input
                    type="number"
                    step="0.01"
                    min="0.8"
                    max="1.1"
                    value={lowerBand}
                    onChange={(e) => setLowerBand(parseFloat(e.target.value))}
                    style={{ width: "70px", marginLeft: "6px" }}
                  />
                </label>

                {/* Timeframe */}
                <label>
                  Timeframe:
                  <select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                    style={{ padding: "6px", marginLeft: "6px" }}
                  >
                    <option value="1d">1 Day</option>
                    <option value="1week">1 Week</option>
                    <option value="4h">4 Hours</option>
                    <option value="3h">3 Hours</option>
                    <option value="2h">2 Hours</option>
                    <option value="1h">1 Hour</option>
                    <option value="45m">45 Minutes</option>
                    <option value="30m">30 Minutes</option>
                    <option value="15m">15 Minutes</option>
                    <option value="5m">5 Minutes</option>
                    <option value="3m">3 Minutes</option>
                    <option value="1m">1 Minute</option>
                  </select>
                </label>

                {/* Symbol */}
                <label>
                  Symbol:
                  <select
                    value={selectedSymbol}
                    onChange={(e) => setSelectedSymbol(e.target.value)}
                    style={{ padding: "6px", marginLeft: "6px" }}
                  >
                    {rows.map((s) => (
                      <option key={s.symbol} value={s.symbol}>
                        {s.symbol} — {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          )}
        </TabPane>
      </Tabs>
    </>
  );
}
